import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { BuyerGroupFormData } from '../../../../Types/AdvanceSearchResultsTypes';
import { actions } from '../../../../Slice/AdvanceSearchSlice';
import { actions as flurryActions } from '../../../../Slice/HelperSlice';
import { valueToObject } from '../../../../Utils/UtilityFunctions';
import {
  selectAdvanceSearchBrmForm,
  selectAdvanceSearchMemberOptions,
  selectAdvanceSearchMemberLoading,
  selectAdvanceSearchMemberHasMore,
  selectAdvanceSearchMemberOffset,
  selectAdvanceSearchMemberOptionsLoaded,
  selectAdvanceSearchMemberSearchTerm,
  selectAdvanceSearchMemberTotal,
  selectAdvanceSearchSharedByOptions,
  selectAdvanceSearchSharedByLoading,
  selectAdvanceSearchSharedByHasMore,
  selectAdvanceSearchSharedByPage,
  selectAdvanceSearchSharedByOptionsLoaded,
  selectAdvanceSearchSharedBySearchTerm,
  selectAdvanceSearchSharedByTotal,
} from '../../../../Selector/AdvanceSearchSelector';
import { FormField } from './FormField';

const host = window.location.href;

const FormContainer = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

interface BrmFormProps {
  isLoading?: boolean;
  brmSizes?: string[];
}

export const BrmForm: React.FC<BrmFormProps> = ({
  isLoading,
  brmSizes,
}) => {
  const dispatch = useDispatch();
  const formData = useSelector(selectAdvanceSearchBrmForm);

  const memberOptions = useSelector(selectAdvanceSearchMemberOptions);
  const memberLoading = useSelector(selectAdvanceSearchMemberLoading);
  const memberHasMore = useSelector(selectAdvanceSearchMemberHasMore);
  const memberOffset = useSelector(selectAdvanceSearchMemberOffset);
  const memberOptionsLoaded = useSelector(selectAdvanceSearchMemberOptionsLoaded);
  const memberTotal = useSelector(selectAdvanceSearchMemberTotal);
  const sharedByOptions = useSelector(selectAdvanceSearchSharedByOptions);
  const sharedByLoading = useSelector(selectAdvanceSearchSharedByLoading);
  const sharedByHasMore = useSelector(selectAdvanceSearchSharedByHasMore);
  const sharedByPage = useSelector(selectAdvanceSearchSharedByPage);
  const sharedByOptionsLoaded = useSelector(selectAdvanceSearchSharedByOptionsLoaded);
  const sharedByTotal = useSelector(selectAdvanceSearchSharedByTotal);
  const memberSearchTerm = useSelector(selectAdvanceSearchMemberSearchTerm);
  const sharedBySearchTerm = useSelector(selectAdvanceSearchSharedBySearchTerm);
  const [sizeId, setSizeId] = useState(0);

  const memberSearchPending = useRef(false);
  const memberSearchKeyword = useRef('');
  const sharedBySearchPending = useRef(false);
  const sharedBySearchKeyword = useRef('');
  const memberSelectedRef = useRef(0);
  const sharedBySelectedRef = useRef(0);

  const updateField = (field: keyof BuyerGroupFormData, value: string | number) => {
    const newData = { ...formData, [field]: value };
    dispatch(actions.setAdvanceSearchBrmForm(newData));
  };

  const brmSizeOptions = useMemo(
    () => (brmSizes ?? []).map((label, i) => ({ id: i + 1, label })),
    [brmSizes],
  );

  const handleSizeChange = (id: number) => {
    setSizeId(id);
    const selectedLabel = brmSizeOptions.find((option) => option.id === id)?.label ?? '';
    updateField('size', selectedLabel);
  };

  const handleMemberOpen = () => {
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('buyer_group_person_dropdown_opened', 0, {
        selection_method: 'Member',
        host,
      }),
    ));
    if (!memberOptionsLoaded && !memberLoading) {
      dispatch(actions.getAdvanceSearchMembers({ offset: 0 }));
    }
  };

  const handleSharedByOpen = () => {
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('buyer_group_person_dropdown_opened', 0, {
        selection_method: 'Shared By',
        host,
      }),
    ));
    if (!sharedByOptionsLoaded && !sharedByLoading) {
      dispatch(actions.getAdvanceSearchSharedBy({ page: 1 }));
    }
  };

  const loadMoreMembers = () => {
    if (!memberLoading && memberHasMore) {
      dispatch(actions.getAdvanceSearchMembers({
        offset: memberOffset,
        searchTerm: memberSearchTerm || undefined,
      }));
    }
  };

  const loadMoreSharedBy = () => {
    if (!sharedByLoading && sharedByHasMore) {
      dispatch(actions.getAdvanceSearchSharedBy({
        page: sharedByPage,
        query: sharedBySearchTerm || undefined,
      }));
    }
  };

  const handleMemberSearch = (term: string) => {
    const searchTerm = term.trim();
    dispatch(actions.setMemberSearchTerm(searchTerm));
    dispatch(actions.getAdvanceSearchMembers({
      offset: 0,
      searchTerm: searchTerm || undefined,
    }));
    if (searchTerm) {
      memberSearchKeyword.current = searchTerm;
      memberSearchPending.current = true;
    }
  };

  const handleSharedBySearch = (term: string) => {
    const searchTerm = term.trim();
    dispatch(actions.setSharedBySearchTerm(searchTerm));
    dispatch(actions.getAdvanceSearchSharedBy({
      page: 1,
      query: searchTerm || undefined,
    }));
    if (searchTerm) {
      sharedBySearchKeyword.current = searchTerm;
      sharedBySearchPending.current = true;
    }
  };

  const handleMemberSelect = (id: number) => {
    updateField('member', id);
    if (!id) {
      memberSelectedRef.current = 0;
      return;
    }
    if (memberSelectedRef.current === id) return;
    memberSelectedRef.current = id;
    const selected = memberOptions.find((option) => option.id === id);
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('buyer_group_person_selected', 0, {
        selection_method: 'Member',
        selected_person_name: selected?.label ?? '',
        personality_type: selected?.personalityType ?? '',
        host,
      }),
    ));
  };

  const handleSharedBySelect = (id: number) => {
    updateField('sharedBy', id);
    if (!id) {
      sharedBySelectedRef.current = 0;
      return;
    }
    if (sharedBySelectedRef.current === id) return;
    sharedBySelectedRef.current = id;
    const selected = sharedByOptions.find((option) => option.id === id);
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('buyer_group_person_selected', 0, {
        selection_method: 'Shared By',
        selected_person_name: selected?.label ?? '',
        personality_type: selected?.personalityType ?? '',
        host,
      }),
    ));
  };

  useEffect(() => {
    const matchedId = brmSizeOptions.find((option) => option.label === formData.size)?.id ?? 0;
    setSizeId(matchedId);
  }, [brmSizeOptions, formData.size]);

  useEffect(() => {
    if (memberSearchPending.current && !memberLoading) {
      memberSearchPending.current = false;
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('buyer_group_person_search', 0, {
          selection_method: 'Member',
          search_keyword: memberSearchKeyword.current,
          result_count: memberTotal,
          host,
        }),
      ));
    }
  }, [memberLoading, memberTotal, dispatch]);

  useEffect(() => {
    if (sharedBySearchPending.current && !sharedByLoading) {
      sharedBySearchPending.current = false;
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('buyer_group_person_search', 0, {
          selection_method: 'Shared By',
          search_keyword: sharedBySearchKeyword.current,
          result_count: sharedByTotal,
          host,
        }),
      ));
    }
  }, [sharedByLoading, sharedByTotal, dispatch]);

  return (
    <FormContainer>
      <FormField
        label="Name"
        value={formData.name}
        onChange={(val) => updateField('name', val)}
        placeholder="Enter buyer group name"
      />
      <FormField
        label="Member"
        type="dropdown"
        value={formData.member}
        onChange={(val) => handleMemberSelect(val as number)}
        placeholder="Select people"
        options={memberOptions}
        hasSearch
        hasMore={!!memberHasMore}
        loadMore={loadMoreMembers}
        isLoading={memberLoading}
        isCardOption
        useRemoteSearch
        onSearchSubmit={handleMemberSearch}
        onDropdownOpen={handleMemberOpen}
        initialSearchTerm={memberSearchTerm}
        searchPlaceholder='Search by Name, Company, Designation'
        clampLabel
      />
      <FormField
        label="Size"
        type="dropdown"
        value={sizeId}
        options={brmSizeOptions}
        onChange={(val) => handleSizeChange(val as number)}
        placeholder="Select buyer group size"
      />
      <FormField
        label="Shared By"
        type="dropdown"
        value={formData.sharedBy}
        onChange={(val) => handleSharedBySelect(val as number)}
        placeholder="Select shared people"
        options={sharedByOptions}
        hasSearch
        hasMore={!!sharedByHasMore}
        loadMore={loadMoreSharedBy}
        isLoading={sharedByLoading}
        useRemoteSearch
        onSearchSubmit={handleSharedBySearch}
        onDropdownOpen={handleSharedByOpen}
        initialSearchTerm={sharedBySearchTerm}
        clampLabel
      />
    </FormContainer>
  );
};
